import React, { useState, useEffect } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Calendar,
  Wallet,
  DollarSign,
  ExternalLink,
  Loader2,
  Info,
  Clock,
  Key
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import adminWithdrawalService from '../../services/adminWithdrawalService';

const WithdrawalDetails = ({ withdrawal, onClose, onAction }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [approvedAmount, setApprovedAmount] = useState(withdrawal.requested_amount);
  const [rejectionReason, setRejectionReason] = useState('');
  const [availableBalance, setAvailableBalance] = useState(null);
  const [detailedWithdrawal, setDetailedWithdrawal] = useState(withdrawal);
  const [fees, setFees] = useState({ baseFee: 0, vat: 0, totalFee: 0 });

  useEffect(() => {
    loadDetailedWithdrawal();
  }, [withdrawal.id]);

  const loadDetailedWithdrawal = async () => {
    try {
      const response = await adminWithdrawalService.getWithdrawalDetails(withdrawal.id);
      if (response.success) {
        setDetailedWithdrawal(response.data.withdrawal);
        setAvailableBalance(response.data.availableBalance);
      }
    } catch (error) {
      console.error('Failed to load detailed withdrawal:', error);
    }
  };

  useEffect(() => {
    if (detailedWithdrawal.currency === 'NGN' && (approvedAmount || detailedWithdrawal.requested_amount)) {
      const amountToUse = approvedAmount || detailedWithdrawal.requested_amount;
      const amount = parseFloat(amountToUse);
      if (!isNaN(amount) && amount > 0) {
        let baseFee = 0;
        if (amount <= 5000) baseFee = 10;
        else if (amount <= 50000) baseFee = 25;
        else baseFee = 50;
        const vat = baseFee * 0.075;
        setFees({ baseFee, vat, totalFee: baseFee + vat });
      } else {
        setFees({ baseFee: 0, vat: 0, totalFee: 0 });
      }
    } else {
      setFees({ baseFee: 0, vat: 0, totalFee: 0 });
    }
  }, [approvedAmount, detailedWithdrawal.currency, detailedWithdrawal.requested_amount]);

  const handleApprove = async () => {
    if (!approvedAmount || approvedAmount <= 0) {
      toast.error('Please enter a valid approved amount');
      return;
    }

    const validation = adminWithdrawalService.validateApproval(detailedWithdrawal, approvedAmount);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setIsLoading(true);
    setActionType('approve');

    try {
      const response = await adminWithdrawalService.approveWithdrawal(
        detailedWithdrawal.id,
        parseFloat(approvedAmount)
      );

      if (response.success) {
        toast.success(`Withdrawal approved for ${adminWithdrawalService.formatAmount(approvedAmount, detailedWithdrawal.currency)}`);
        onAction();
      }
    } catch (error) {
      console.error('Failed to approve withdrawal:', error);
      toast.error(error.message || 'Failed to approve withdrawal');
    } finally {
      setIsLoading(false);
      setActionType(null);
    }
  };



  const handleReject = async () => {
    try {
      const validatedReason = adminWithdrawalService.validateRejectionReason(rejectionReason);

      setIsLoading(true);
      setActionType('reject');

      const response = await adminWithdrawalService.rejectWithdrawal(
        detailedWithdrawal.id,
        validatedReason
      );

      if (response.success) {
        toast.success('Withdrawal rejected successfully');
        onAction();
      }
    } catch (error) {
      console.error('Failed to reject withdrawal:', error);
      toast.error(error.message || 'Failed to reject withdrawal');
    } finally {
      setIsLoading(false);
      setActionType(null);
    }
  };

  const statusInfo = adminWithdrawalService.formatStatus(detailedWithdrawal.status);
  const priorityInfo = adminWithdrawalService.getPriority(detailedWithdrawal);
  const canTakeAction = detailedWithdrawal.status === 'pending';

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Withdrawal Review</h2>
            <p className="text-sm text-gray-600">ID: {detailedWithdrawal.id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Priority */}
          <div className="flex items-center gap-4">
            <span className={`px-3 py-2 rounded-lg text-sm font-medium border ${statusInfo.bgClass}`}>
              {statusInfo.label}
            </span>
            <span className={`px-3 py-2 rounded-lg text-sm font-medium border ${priorityInfo.class}`}>
              {priorityInfo.label}
            </span>
            {detailedWithdrawal.auto_approved && (
              <span className="px-3 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Auto-approved
              </span>
            )}
          </div>

          {/* Project Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Project Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Project Title</p>
                <p className="text-gray-900">{detailedWithdrawal.project?.title || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Category</p>
                <p className="text-gray-900">{detailedWithdrawal.project?.category || 'N/A'}</p>
              </div>
              <div className="col-span-1 sm:col-span-2 mt-2 pt-3 border-t border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Financial Breakdown ({detailedWithdrawal.currency})
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Total Raised</p>
                    <p className="text-gray-900">
                      {availableBalance ? adminWithdrawalService.formatAmount(availableBalance.gross, detailedWithdrawal.currency) : '...'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Platform Fees</p>
                    <p className="text-red-600">
                      -{availableBalance ? adminWithdrawalService.formatAmount(availableBalance.fees, detailedWithdrawal.currency) : '...'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Already Withdrawn</p>
                    <p className="text-gray-900">
                      -{availableBalance ? adminWithdrawalService.formatAmount(availableBalance.withdrawn, detailedWithdrawal.currency) : '...'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Pending Requests</p>
                    <p className="text-yellow-600">
                      -{availableBalance ? adminWithdrawalService.formatAmount(availableBalance.pending, detailedWithdrawal.currency) : '...'}
                    </p>
                  </div>
                  <div className="col-span-2 pt-2 border-t border-gray-200 mt-1">
                    <div className="flex justify-between items-center bg-green-50 p-2 rounded-lg border border-green-100">
                      <p className="text-sm font-bold text-gray-900">Available to Cover Request</p>
                      <p className="text-lg font-bold text-green-600">
                        {availableBalance !== null
                          ? adminWithdrawalService.formatAmount(availableBalance.available, detailedWithdrawal.currency)
                          : 'Loading...'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Owner Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Project Owner</h3>
            <div className="flex items-center gap-4 mb-4">
              {detailedWithdrawal.owner?.profile_image ? (
                <img
                  src={detailedWithdrawal.owner.profile_image}
                  alt={detailedWithdrawal.owner.full_name || detailedWithdrawal.owner.pi_username}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {detailedWithdrawal.owner?.full_name || detailedWithdrawal.owner?.pi_username || 'Unknown'}
                </p>
                <p className="text-sm text-gray-600">{detailedWithdrawal.owner?.email || 'No email'}</p>
                {detailedWithdrawal.owner?.badge_level && (
                  <p className="text-sm text-blue-600 font-medium">
                    {detailedWithdrawal.owner.badge_level} Badge
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Total Donated</p>
                <p className="text-gray-900">
                  {adminWithdrawalService.formatAmount(detailedWithdrawal.owner?.total_donated || 0)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Member Since</p>
                <p className="text-gray-900">
                  {detailedWithdrawal.owner?.created_at
                    ? new Date(detailedWithdrawal.owner.created_at).toLocaleDateString()
                    : 'Unknown'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Withdrawal Details */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Withdrawal Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Requested Amount</p>
                <p className="text-xl font-bold text-gray-900">
                  {adminWithdrawalService.formatAmount(detailedWithdrawal.requested_amount, detailedWithdrawal.currency)}
                </p>
              </div>

              {detailedWithdrawal.currency === 'NGN' && (detailedWithdrawal.metadata?.transferFee || fees.totalFee > 0) && (
                <div className="col-span-1 sm:col-span-2 mt-2 p-3 bg-white rounded border border-blue-100 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <div>
                    <span className="text-gray-500 mr-2">Transfer Fee:</span>
                    <span className="font-semibold text-gray-900">₦{(detailedWithdrawal.metadata?.feeBreakdown?.baseFee || fees.baseFee).toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 mr-2">VAT (7.5%):</span>
                    <span className="font-semibold text-gray-900">₦{(detailedWithdrawal.metadata?.feeBreakdown?.vat || fees.vat).toFixed(2)}</span>
                  </div>
                  <div className="border-l border-blue-200 pl-4">
                    <span className="text-gray-500 mr-2">Total Project Cost:</span>
                    <span className="font-bold text-blue-600">
                      ₦{new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format((parseFloat(approvedAmount) + (detailedWithdrawal.metadata?.transferFee || fees.totalFee)))}
                    </span>
                  </div>
                </div>
              )}

              {detailedWithdrawal.currency === 'NGN' ? (
                <>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Bank Name</p>
                    <p className="text-gray-900">{detailedWithdrawal.bank_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Account Number</p>
                    <p className="text-gray-900 font-mono">{detailedWithdrawal.account_number}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Account Name</p>
                    <p className="text-gray-900">{detailedWithdrawal.account_name || 'N/A'}</p>
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-sm font-medium text-gray-700">Recipient Wallet</p>
                  <p className="text-gray-900 font-mono text-sm break-all">
                    {detailedWithdrawal.recipient_wallet}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-700">Requested Date</p>
                <p className="text-gray-900">{formatDate(detailedWithdrawal.requested_at)}</p>
              </div>
              {detailedWithdrawal.approved_at && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Approved Date</p>
                  <p className="text-gray-900">{formatDate(detailedWithdrawal.approved_at)}</p>
                </div>
              )}
            </div>

            {/* Request Reason */}
            {detailedWithdrawal.request_reason && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Reason for Withdrawal</p>
                <div className="bg-white rounded border p-3">
                  <p className="text-gray-900 text-sm">{detailedWithdrawal.request_reason}</p>
                </div>
              </div>
            )}

            {/* Rejection Reason */}
            {detailedWithdrawal.status === 'rejected' && detailedWithdrawal.rejection_reason && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Rejection Reason</p>
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-red-800 text-sm">{detailedWithdrawal.rejection_reason}</p>
                </div>
              </div>
            )}
          </div>

          {/* Transaction Information */}
          {detailedWithdrawal.transaction_hash && (
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Transaction Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {detailedWithdrawal.currency === 'NGN' ? 'Flutterwave Reference' : 'Transaction Hash'}
                  </p>
                  <p className="text-gray-900 font-mono text-sm break-all">
                    {detailedWithdrawal.transaction_hash}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Completed Date</p>
                  <p className="text-gray-900">
                    {detailedWithdrawal.completed_at ? formatDate(detailedWithdrawal.completed_at) : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="mt-3">
                {detailedWithdrawal.currency === 'PI' ? (
                  <a
                    href={`https://pi-blockchain.net/tx/${detailedWithdrawal.transaction_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on Pi Blockchain
                  </a>
                ) : (
                  <span className="text-sm text-gray-500 italic">
                    Transfer reference verified via Flutterwave API
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Admin Actions */}
          {detailedWithdrawal.status === 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Admin Actions</h3>

              {/* Approval Section */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approved Amount ({detailedWithdrawal.currency === 'NGN' ? '₦' : 'π'})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min={detailedWithdrawal.currency === 'NGN' ? "100" : "10"}
                    max={detailedWithdrawal.requested_amount}
                    value={approvedAmount}
                    onChange={(e) => setApprovedAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 bg-gray-100 text-gray-500 rounded-lg focus:outline-none cursor-not-allowed"
                    disabled={true}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum: {adminWithdrawalService.formatAmount(detailedWithdrawal.requested_amount, detailedWithdrawal.currency)}
                  </p>
                  {fees.totalFee > 0 && (
                    <p className="text-xs text-blue-600 mt-1 font-medium italic">
                      + ₦{fees.totalFee.toFixed(2)} transfer charge will be debited from project balance
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason (if rejecting)
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter detailed reason for rejection (minimum 10 characters)..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required for rejection (minimum 10 characters)
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleApprove}
                    disabled={isLoading || !approvedAmount}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 
                               bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white 
                               rounded-lg transition-colors focus:outline-none focus:ring-2 
                               focus:ring-green-500 focus:ring-offset-2"
                  >
                    {isLoading && actionType === 'approve' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Approve Withdrawal
                  </button>

                  <button
                    onClick={handleReject}
                    disabled={isLoading || !rejectionReason || rejectionReason.trim().length < 10}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 
                               bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white 
                               rounded-lg transition-colors focus:outline-none focus:ring-2 
                               focus:ring-red-500 focus:ring-offset-2"
                  >
                    {isLoading && actionType === 'reject' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    Reject Withdrawal
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Important Notes */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Review Guidelines:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Verify the project owner's identity and legitimacy</li>
                  <li>• Ensure the withdrawal amount is reasonable for the project</li>
                  <li>• Check that sufficient funds are available</li>
                  <li>• Validate the recipient wallet address format</li>
                  <li>• Consider the project's completion status and milestones</li>
                  <li>• Auto-approved withdrawals (≤10,000 π) are processed automatically</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 
                         transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              disabled={isLoading}
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>

    </div>
  );
};

export default WithdrawalDetails;
